import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';
import ImageColumGrid from '../components/common/ImageColumGrid';
import { AppContext } from '../contexts/AppContext';
import Box from '@mui/material/Box';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocalPizza from '@mui/icons-material/LocalPizza';
import Snackbar from '@mui/material/Snackbar';
import posthog from 'posthog-js';
import useRestricted from '../hooks/useRestricted';
import { Permission } from '../services/UserService';
import LoadingButton from '@mui/lab/LoadingButton';
import CircularProgress from '@mui/material/CircularProgress';
import { TrainingRun } from '../services/TrainingRunsService';
import TrainingRunStep3Details from '../components/trainingRuns/TrainingRunStep3Details';

const EditTrainingRun: React.FC = observer(() => {
  const permission = useRestricted();

  const intl = useIntl();
  const navigate = useNavigate();

  React.useEffect(() => {
    if (permission === Permission.free) {
      navigate('/');
    }
  }, [permission, navigate]);
  const { id } = useParams<{ id: string }>();

  const { userService, trainingRunsService } = React.useContext(AppContext);

  const [trainingRunDraft, setTrainingRunDraft] = React.useState<Partial<TrainingRun> | null>(null);
  const [error, setError] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [isLoadingRun, setIsLoadingRun] = React.useState<boolean>(true);

  React.useEffect(() => {
    if (!id) {
      navigate('/nutritional-training');
      return;
    }

    const loadRun = async () => {
      setIsLoadingRun(true);
      const run = await trainingRunsService.loadTrainingRun(id);
      if (run) {
        setTrainingRunDraft(run);
      } else {
        setError('error.trainingRunNotFound');
        navigate('/nutritional-training');
      }
      setIsLoadingRun(false);
    };

    loadRun();
  }, [id, trainingRunsService, navigate]);

  const handleBack = () => {
    navigate('/nutritional-training');
  };

  const handleSubmit = async () => {
    if (!trainingRunDraft || !userService.user?.id || !id) {
      setError('error.missingRequiredData');
      return;
    }

    setIsLoading(true);

    try {
      const updatedTrainingRun: TrainingRun = {
        id: id,
        name: trainingRunDraft.name || '',
        date: trainingRunDraft.date || new Date(),
        distance: trainingRunDraft.distance || 0,
        time: trainingRunDraft.time || 0,
        elevationGain: trainingRunDraft.elevationGain || 0,
        elevationLoss: trainingRunDraft.elevationLoss || 0,
        nutrition: trainingRunDraft.nutrition || [],
        performance: trainingRunDraft.performance || 5,
        gutIssues: trainingRunDraft.gutIssues || 5,
        temperature: trainingRunDraft.temperature || 5,
        notes: trainingRunDraft.notes || '',
        source: trainingRunDraft.source || 'manual',
        userId: userService.user.id,
        createdAt: trainingRunDraft.createdAt || new Date(),
        modifiedAt: new Date(),
      };

      await trainingRunsService.saveTrainingRun(updatedTrainingRun);

      posthog.capture('training_run_updated', {
        training_run_id: updatedTrainingRun.id,
      });

      navigate('/nutritional-training');

    } catch (error) {
      console.error("Error updating training run:", error);
      setError('error.savingTrainingRun');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoadingRun) {
    return <Box height="100vh" width="100%" display="flex" alignItems="center" justifyContent="center"><CircularProgress /></Box>;
  }

  const mainComponent = <Box
    sx={{
      my: 8,
      mx: 4,
      display: 'flex',
      flexDirection: 'column',
      textAlign: 'center',
    }}
  >
    <Button startIcon={<ArrowBackIcon />} sx={{ alignSelf: "flex-start" }}
      onClick={handleBack}
      color="inherit"
    >
      {intl.formatMessage({ id: 'action.back' })}
    </Button>

    <Avatar sx={{ marginX: "auto", marginY: 1, bgcolor: 'secondary.main' }}>
      <LocalPizza />
    </Avatar>
    <Typography component="h1" variant="h5" gutterBottom>
      {intl.formatMessage({ id: 'trainingRun.editTitle' })}
    </Typography>

    {trainingRunDraft && (
      <TrainingRunStep3Details
        trainingRun={trainingRunDraft}
        onUpdate={setTrainingRunDraft}
      />
    )}

    <LoadingButton
      loading={isLoading}
      fullWidth
      variant="contained"
      onClick={handleSubmit}
      disabled={!trainingRunDraft || isLoading}
      sx={{ my: 1 }}
    >
      {intl.formatMessage({ id: 'action.save' })}
    </LoadingButton>
  </Box>

  return (
    <>
      <ImageColumGrid image={`${process.env.PUBLIC_URL}/images/auth-background.jpg`}>
        {mainComponent}
      </ImageColumGrid>
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        message={error ? intl.formatMessage({ id: error }) : ''}
      />
    </>
  );
});

export default EditTrainingRun;
